import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, type Issue } from '@/api/client';
import { Sparkline } from '@/components/Sparkline';
import { ago, compactCount, formatCount } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { inTab, ISSUE_RANGES, type IssueTab, STATUS_LABEL, TABS } from '@/lib/issues';
import { navigate } from '@/lib/router';
import { shellKey, usePageCommands, useShell } from '@/lib/shell';
import { QueryHighlight } from '@/lib/text';

type Sort = 'last' | 'count' | 'users' | 'first';

function readUrl(): { range: string; tab: IssueTab; q: string; sort: Sort } {
    const params = new URLSearchParams(location.search);
    const range = params.get('range') ?? '24h';
    const tab = params.get('tab') as IssueTab | null;
    const sort = params.get('sort') as Sort | null;

    return {
        range: ISSUE_RANGES.some(([key]) => key === range) ? range : '24h',
        tab: tab && TABS.some(([key]) => key === tab) ? tab : 'open',
        q: params.get('q') ?? '',
        sort: sort && ['last', 'count', 'users', 'first'].includes(sort) ? sort : 'last',
    };
}

const SORTS: Readonly<Record<Sort, (a: Issue, b: Issue) => number>> = {
    last: (a, b) => b.lastSeen - a.lastSeen,
    count: (a, b) => b.count - a.count,
    users: (a, b) => b.users - a.users || b.count - a.count,
    first: (a, b) => b.firstEver - a.firstEver,
};

// The errors of an environment, grouped: what broke, how often, for how many people, since when, and what the team
// decided about it. A resolved issue that happens again comes back as REGRESSED.
export function Issues(): ReactNode {
    const { env, say } = useShell();
    const queryClient = useQueryClient();
    const [initial] = useState(readUrl);
    const [range, setRange] = useState(initial.range);
    const [tab, setTab] = useState<IssueTab>(initial.tab);
    const [sort, setSort] = useState<Sort>(initial.sort);
    const [input, setInput] = useState(initial.q);
    const [q, setQ] = useState(initial.q);
    const [anchor, setAnchor] = useState(() => Date.now());
    const now = useNow(15_000);
    const span = ISSUE_RANGES.find(([key]) => key === range)?.[2] ?? 24 * 3600_000;
    const scope = useMemo(() => ({ env, q, from: anchor - span, to: anchor }), [env, q, anchor, span]);

    useEffect(() => {
        const timer = setTimeout(() => setQ(input.trim()), 400);

        return () => clearTimeout(timer);
    }, [input]);
    // The list follows the clock: every minute it reads again up to now.
    useEffect(() => {
        const timer = setInterval(() => setAnchor(Date.now()), 60_000);

        return () => clearInterval(timer);
    }, []);
    useEffect(() => {
        const params = new URLSearchParams({ env, range, tab, sort });
        if (q) {
            params.set('q', q);
        }
        history.replaceState(null, '', `/issues?${params.toString()}`);
    }, [env, range, tab, sort, q]);

    const issues = useQuery({
        queryKey: ['issues', scope],
        queryFn: ({ signal }) => api.issues(scope, signal),
        enabled: env !== '',
        placeholderData: (previous) => previous,
    });
    const all = useMemo(() => issues.data?.issues ?? [], [issues.data]);
    const counts = useMemo(() => Object.fromEntries(TABS.map(([key]) => [key, all.filter((issue) => inTab(issue.status, key)).length])), [all]);
    const shown = useMemo(() => all.filter((issue) => inTab(issue.status, tab)).sort(SORTS[sort]), [all, tab, sort]);
    const setState = useMutation({
        mutationFn: ({
            issue,
            status,
            mutedHours,
        }: {
            issue: Issue;
            status: 'open' | 'acknowledged' | 'resolved' | 'muted';
            mutedHours?: number | null;
        }) => api.setIssueState(issue.fingerprint, { env, status, mutedHours: mutedHours ?? null }),
        onSuccess: async (_, { issue, status }) => {
            say(`${issue.title}: ${status === 'open' ? 'reopened' : status}`);
            await queryClient.invalidateQueries({ queryKey: ['issues'] });
        },
        onError: (error) => say(error instanceof ApiError ? error.message : 'Not saved'),
    });
    const open = useCallback((issue: Issue): void => navigate(`/issues/${issue.fingerprint}?env=${env}&range=${range}`), [env, range]);

    // The keyboard: J and K move through the list, Enter opens, A, R and M decide; / narrows down.
    const filterRef = useRef<HTMLInputElement>(null);
    const [cursor, setCursor] = useState(-1);
    const at = Math.min(cursor, shown.length - 1);
    const { mutate: decide } = setState;
    useEffect(() => {
        document.querySelector('.ptable.issues tr.cursor')?.scrollIntoView({ block: 'nearest' });
    }, [at]);
    useEffect(() => {
        const onKey = (event: KeyboardEvent): void => {
            const target = event.target as HTMLElement;
            if (
                shellKey(event) ||
                document.querySelector('.scrim') ||
                ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName) ||
                event.metaKey ||
                event.ctrlKey ||
                event.altKey
            ) {
                return;
            }
            const issue = shown[at];
            const actions: Record<string, () => void> = {
                j: () => setCursor(Math.min(shown.length - 1, at + 1)),
                k: () => setCursor(Math.max(0, at - 1)),
                Enter: () => issue && open(issue),
                a: () => issue && decide({ issue, status: 'acknowledged' }),
                r: () => issue && decide({ issue, status: 'resolved' }),
                m: () => issue && decide({ issue, status: 'muted', mutedHours: 24 }),
                '/': () => filterRef.current?.focus(),
                Escape: () => setCursor(-1),
            };
            const action = event.shiftKey && event.key === '/' ? undefined : actions[event.key];
            if (action) {
                event.preventDefault();
                action();
            }
        };
        window.addEventListener('keydown', onKey);

        return () => window.removeEventListener('keydown', onKey);
    }, [shown, at, open, decide]);

    usePageCommands([
        ...TABS.map(([key, label]) => ({ icon: '◆', label: `Issues · ${label.toLowerCase()}`, run: () => setTab(key) })),
        ...ISSUE_RANGES.map(([key, label]) => ({ icon: '◷', label: `Issues of the last ${label.toLowerCase()}`, run: () => setRange(key) })),
    ]);

    return (
        <div className="page page-scroll">
            <div className="page-head">
                <div>
                    <h1>Issues</h1>
                    <p>
                        Every error, grouped by its fingerprint: the exception, its message without ids, the first frame of the app. A resolved issue
                        that happens again comes back as regressed.
                    </p>
                </div>
                <div className="seg">
                    {ISSUE_RANGES.map(([key, label]) => (
                        <button key={key} className={key === range ? 'on' : ''} onClick={() => setRange(key)}>
                            {label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="issues-bar">
                <div className="tabs-row">
                    {TABS.map(([key, label]) => (
                        <button key={key} className={key === tab ? 'on' : ''} onClick={() => setTab(key)}>
                            {label} <span className="count">{counts[key] ?? 0}</span>
                        </button>
                    ))}
                </div>
                <input
                    ref={filterRef}
                    className="filter"
                    placeholder="Narrow down: service:api · exception:AxiosError · path:/bids"
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    spellCheck={false}
                />
            </div>
            <div className={`panel ${issues.isFetching ? 'loading' : ''}`}>
                <table className="ptable issues">
                    <thead>
                        <tr className="micro">
                            <th>State</th>
                            <th>Issue</th>
                            <th className="opt">Services</th>
                            <th className="opt">Last {ISSUE_RANGES.find(([key]) => key === range)?.[1]}</th>
                            {(
                                [
                                    ['count', 'Events'],
                                    ['users', 'Users'],
                                    ['last', 'Last seen'],
                                    ['first', 'First seen'],
                                ] as Array<[Sort, string]>
                            ).map(([key, label]) => (
                                <th
                                    key={key}
                                    className={`sortable ${key === 'count' || key === 'users' ? 'num' : ''} ${key === 'users' || key === 'first' ? 'opt' : ''} ${sort === key ? 'on' : ''}`}
                                    onClick={() => setSort(key)}
                                >
                                    {label} {sort === key ? '▾' : ''}
                                </th>
                            ))}
                            <th className="opt" />
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((issue, index) => (
                            <tr key={issue.fingerprint} className={`clickable ${index === at ? 'cursor' : ''}`} onClick={() => open(issue)}>
                                <td>
                                    <span className={`status ${issue.status}`}>{STATUS_LABEL[issue.status]}</span>
                                </td>
                                <td className="issue-cell">
                                    <div className="issue-title">
                                        {issue.isNew && <span className="newbadge">New</span>}
                                        <b>{issue.title}</b>
                                        {issue.exception && issue.logger && <span className="where">{issue.logger}</span>}
                                    </div>
                                    <div className="issue-template">{issue.template}</div>
                                </td>
                                <td className="mono small opt">{issue.services.join(' · ')}</td>
                                <td className="opt">
                                    <Sparkline
                                        values={issue.sparkline}
                                        tone={issue.status === 'resolved' || issue.status === 'muted' ? 'muted' : 'critical'}
                                    />
                                </td>
                                <td className="num mono" title={formatCount(issue.count)}>
                                    {compactCount(issue.count)}
                                </td>
                                <td className="num mono opt">{compactCount(issue.users)}</td>
                                <td>{ago(issue.lastSeen, now)} ago</td>
                                <td className="opt" title={`commit ${issue.firstEverCommit.slice(0, 9)}`}>
                                    {ago(issue.firstEver, now)} ago
                                </td>
                                <td className="row-actions opt" onClick={(event) => event.stopPropagation()}>
                                    {issue.status === 'open' || issue.status === 'regressed' ? (
                                        <button
                                            className="act"
                                            onClick={() => setState.mutate({ issue, status: 'acknowledged' })}
                                            title="Someone is on it"
                                        >
                                            Ack
                                        </button>
                                    ) : null}
                                    {issue.status !== 'resolved' ? (
                                        <button
                                            className="act"
                                            onClick={() => setState.mutate({ issue, status: 'resolved' })}
                                            title="Fixed: it comes back as regressed if it happens again"
                                        >
                                            Resolve
                                        </button>
                                    ) : null}
                                    {issue.status !== 'muted' ? (
                                        <button
                                            className="act"
                                            onClick={() => setState.mutate({ issue, status: 'muted', mutedHours: 24 })}
                                            title="Out of the way for 24 hours"
                                        >
                                            Mute 24h
                                        </button>
                                    ) : null}
                                    {issue.status === 'resolved' || issue.status === 'muted' || issue.status === 'acknowledged' ? (
                                        <button className="act" onClick={() => setState.mutate({ issue, status: 'open' })}>
                                            Reopen
                                        </button>
                                    ) : null}
                                </td>
                            </tr>
                        ))}
                        {shown.length === 0 && !issues.isLoading && (
                            <tr>
                                <td colSpan={9} className="ptable-empty">
                                    {all.length === 0 ? (
                                        <>
                                            <b>No error</b> in {env} in the last {ISSUE_RANGES.find(([key]) => key === range)?.[1].toLowerCase()}
                                            {q && (
                                                <>
                                                    {' '}
                                                    for <QueryHighlight q={q} />
                                                </>
                                            )}
                                            .
                                        </>
                                    ) : (
                                        'Nothing in this tab.'
                                    )}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
